import { useCallback, useSyncExternalStore } from 'react';
import type { DevServerId, DevServersSnapshot } from '../lib/devServersRegistry';

const EMPTY: DevServersSnapshot = { available: false, servers: {} };

type StoreState = {
  snapshot: DevServersSnapshot;
  startErrors: Partial<Record<DevServerId, string>>;
};

let snapshot: DevServersSnapshot = EMPTY;
let startErrors: Partial<Record<DevServerId, string>> = {};
let cachedState: StoreState = { snapshot: EMPTY, startErrors: {} };

const listeners = new Set<() => void>();
let pollTimer: ReturnType<typeof setInterval> | null = null;
let subscriberCount = 0;

function publish() {
  cachedState = { snapshot, startErrors: { ...startErrors } };
  listeners.forEach((l) => l());
}

async function refreshShared() {
  if (!import.meta.env.DEV) {
    snapshot = EMPTY;
    publish();
    return;
  }
  try {
    const res = await fetch('/api/dev-servers/status');
    if (res.ok) {
      snapshot = (await res.json()) as DevServersSnapshot;
      publish();
    }
  } catch {
    snapshot = EMPTY;
    publish();
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  subscriberCount += 1;
  if (subscriberCount === 1) {
    void refreshShared();
    pollTimer = setInterval(() => void refreshShared(), 3000);
  }
  return () => {
    listeners.delete(listener);
    subscriberCount -= 1;
    if (subscriberCount === 0 && pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };
}

function getSnapshot() {
  return cachedState;
}

export function useDevServers() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const refresh = useCallback(() => void refreshShared(), []);

  const start = useCallback(async (serverId: DevServerId) => {
    if (!import.meta.env.DEV) return;
    const nextErrors = { ...startErrors };
    delete nextErrors[serverId];
    startErrors = nextErrors;
    publish();

    try {
      const res = await fetch('/api/dev-servers/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: serverId }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) {
        startErrors = { ...startErrors, [serverId]: data.error ?? 'Failed to start' };
        publish();
      } else {
        const burst = setInterval(() => void refreshShared(), 1000);
        setTimeout(() => clearInterval(burst), 20000);
      }
      void refreshShared();
    } catch (err) {
      startErrors = {
        ...startErrors,
        [serverId]: err instanceof Error ? err.message : 'Failed to start',
      };
      publish();
    }
  }, []);

  return {
    snapshot: state.snapshot,
    startErrors: state.startErrors,
    refresh,
    start,
  };
}
